import { useEffect, useRef } from 'react';

import {
  bookingStatusSelect,
  calendarDate,
  getBookingStatus,
  getPharmacyCalendar,
  paymentResetValue,
  resetPharmacyApprovedBooking,
  setPaymentLoad,
  StripePaymentStatus,
} from '@pharmaplan/common';
import { useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';

import usePharmacistProfile from '../PharmacistProfile/usePharmacistProfile';
import { resetDrawer } from '../../actions/drawerActions';

import { useAppSelector } from '../useAppSelector';
import { useAppDispatch } from '../useAppDispatch';

import usePaymentCheckStatus from './usePaymentCheckStatus';
import { showError } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../localization';

// IF BOOKING COMPLETED IS TRUE THEN SUCCESS
// IF PAYMENT PROCESSED IS TRUE BUT BOOKING COMPLETED FALSE THEN HOLD FOR SOME DURATION AND CHECK BOOKING STATUS
// IF BOTH ARE FALSE THEN USE SECRET TO CHECK PAYMENT STATUS AND HANDLE PAYMENT STATUS SCENARIOS

enum StatusToCheck {
  paymentHeld,
  bookingPending,
}

const MAX_CHECKS = 60;

const useCheckBookingUtils = (bookingId: string, secret: string) => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();

  const { showApproveSuccessModal } = usePharmacistProfile({});
  const { checkPaymentStatus } = usePaymentCheckStatus();

  const bookingStatus = useAppSelector(bookingStatusSelect);
  const cDate = useAppSelector(calendarDate);

  const checkStatus = useRef<StatusToCheck | null>(null);
  const maxChecks = useRef(0);

  const { approved } = bookingStatus ?? {};
  const approveRef = useRef(false);

  const clearData = () => {
    dispatch(resetDrawer());
    dispatch(setPaymentLoad(false));
    dispatch(paymentResetValue(['bookingStatus']));
    dispatch(resetPharmacyApprovedBooking());
    checkStatus.current = null;
    maxChecks.current = 0;
  };

  const handleSuccessfulBooking = () => {
    dispatch(getPharmacyCalendar(cDate));
    showApproveSuccessModal();
    clearData();
    toast.dismiss();
  };

  const keepCheckingBooking = () => {
    dispatch(getBookingStatus(bookingId)).then(() => {
      if (maxChecks.current > MAX_CHECKS) {
        clearData();
        showError(dispatch, strings.paymentInProgressIssue);
        return;
      }
      if (approveRef.current) {
        handleSuccessfulBooking();
        checkStatus.current = null;
        return;
      }

      if (checkStatus.current === StatusToCheck.bookingPending) {
        maxChecks.current += 1;
        setTimeout(keepCheckingBooking, 1000);
      }
    });
  };

  const onSuccessfullPayment = () => {
    checkStatus.current = StatusToCheck.bookingPending;
    keepCheckingBooking();
  };

  const confirm3dsPayment = () => {
    stripe
      ?.confirmPayment({
        clientSecret: secret ?? '',
        redirect: 'if_required',
        confirmParams: { return_url: process.env.REACT_APP_REDIRECT_URL },
      })
      .then((res) => {
        const { paymentIntent } = res ?? {};
        if (paymentIntent?.status === StripePaymentStatus.requiresAction) {
          stripe
            ?.handleNextAction({ clientSecret: secret })
            .then(checkPaymentStatus(onSuccessfullPayment, clearData));
        } else {
          checkPaymentStatus(onSuccessfullPayment, clearData)(res);
        }
      });
  };

  useEffect(() => {
    approveRef.current = approved;
  }, [approved]);

  return {
    checkStatus,
    confirm3dsPayment,
    keepCheckingBooking,
    StatusToCheck,
    handleSuccessfulBooking,
  };
};

export default useCheckBookingUtils;
